import {
  Avatar,
  AvatarGroup,
  Box,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import { useRef } from "react";
import { useUserData } from "../../../auth/useUserData.js";
import { getInitials } from "../../../commons/Avatar.js";
import { UserAvatar } from "../../users/UserAvatar.js";
import {
  useActiveDocumentViewersChangedSubscription,
  useDocumentViewerAvatarsQuery,
} from "./documentViewerAvatars.generated.js";

// Real avatars shown before the rest collapse into a "+N" surplus chip.
// AvatarGroup's `max` counts the surplus as one of its slots, so `+1` below caps
// the row at this many avatars *plus* the chip. The one boundary that does not
// follow: at exactly one viewer over, MUI widens `max` by one rather than hiding
// a single avatar behind a chip that would read "+1" — so N+1 viewers render as
// N+1 avatars and no chip. That is deliberate on MUI's side and better here too
// (a real face beats a "+1"), and either way the row is capped at N+1 circles.
const ROW_SM = {
  maxVisibleAvatars: 3,
  spacing: "small",
  avatar: { width: 24, height: 24, fontSize: "0.75rem" },
} as const;
const ROW_MD = {
  maxVisibleAvatars: 5,
  spacing: "medium",
  avatar: { width: 32, height: 32, fontSize: "0.875rem" },
} as const;
// Small grace period so moving the pointer from the avatars onto the tooltip
// (across the gap) doesn't dismiss it.
const TOOLTIP_LEAVE_DELAY_MS = 100;

interface Props {
  documentId: string;
}

export const DocumentViewerAvatars = ({ documentId }: Props) => {
  const { t } = useTranslate("Global");
  const { currentUser } = useUserData();
  const isMaxPhone = useMediaQuery(theme => theme.breakpoints.down("sm"));

  const { data: subscriptionData } =
    useActiveDocumentViewersChangedSubscription({
      variables: { documentId, documentType: "QUOTE" },
    });

  // The subscription emits every viewer including the current user; show only
  // who *else* has the document open. Until the current user id is known
  // (MeQuery still loading) we can't exclude self, so render nothing rather than
  // briefly show the user's own avatar.
  const currentUserId = currentUser?.id;
  const otherViewerIds = currentUserId
    ? (subscriptionData?.activeDocumentViewersChanged ?? []).filter(
        id => id !== currentUserId
      )
    : [];

  // Query the union of everyone seen on this document, not the live set. There
  // is no type policy for `organisationMemberships`, so the default `keyArgs`
  // put `filters.ids` in the cache key: every distinct id list is a separate
  // cache field, and keying on the live set would miss (and refetch over the
  // network) on every single join *and* leave — [u1] → [u1,u2] → [u1] is three
  // fetches for two already-normalised users. The union only ever grows, so it
  // changes once per genuinely new viewer.
  //
  // Scoped to `documentId`, and reset when it changes: React Router reuses this
  // component instance when only the route param moves, so an unscoped union
  // would carry the previous document's viewers into this one's query. Those
  // stale ids compete for the query's `limit: 100` under a `firstname asc` sort,
  // which can push a genuinely current viewer off the page so their avatar never
  // renders. Sized by the distinct viewers of *one* document, so the cap is out
  // of reach in practice.
  const everSeenViewerIds = useRef({ documentId, ids: new Set<string>() });
  if (everSeenViewerIds.current.documentId !== documentId) {
    everSeenViewerIds.current = { documentId, ids: new Set() };
  }
  for (const id of otherViewerIds) everSeenViewerIds.current.ids.add(id);
  const queriedIds = [...everSeenViewerIds.current.ids].sort();

  const { data: membersData } = useDocumentViewerAvatarsQuery({
    variables: { ids: queriedIds },
    skip: queriedIds.length === 0,
    fetchPolicy: "cache-first",
  });

  // Render in the query's stable order (firstname asc), filtered to the current
  // viewer set. Filtering by the live subscription IDs removes a departed viewer
  // immediately (before the query refetches), while the query order keeps the
  // avatars from reshuffling as viewers' presence is re-scored server-side.
  const otherViewerIdSet = new Set(otherViewerIds);
  const members = (
    getDataOrNull(membersData?.organisationMemberships)?.edges ?? []
  )
    .map(edge => edge.node)
    .filter(node => otherViewerIdSet.has(node.id));

  if (members.length === 0) {
    return null;
  }

  const row = isMaxPhone ? ROW_SM : ROW_MD;

  // AvatarGroup overlaps its avatars via sibling margins, so individual avatars
  // can't be wrapped in tooltips without breaking the overlap. Instead the whole
  // group carries one tooltip listing every viewer by name. MUI's Tooltip is
  // interactive by default, so it owns the hover/focus listeners, the anchoring
  // and the grace period that let the pointer cross onto the list.
  //
  // `describeChild` makes the popper the trigger's *description* (`aria-describedby`
  // while open). Without it, a non-string `title` makes MUI reach for the trigger's
  // *name* instead — `aria-labelledby` set only while open — leaving the focusable
  // wrapper nameless whenever the tooltip is closed. The `role`/`aria-label` below
  // supply that name, so it holds open or closed.
  return (
    <Tooltip
      describeChild
      placement="bottom-end"
      leaveDelay={TOOLTIP_LEAVE_DELAY_MS}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: 3,
            p: 1,
            maxWidth: 280,
          },
        },
      }}
      title={
        <Stack spacing={0.5}>
          {members.map(member => (
            <Box
              key={member.id}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <UserAvatar userAvatar={member} size="xs" />
              <Typography variant="body2" noWrap>
                {member.fullname}
              </Typography>
            </Box>
          ))}
        </Stack>
      }
    >
      <Box
        sx={{ display: "inline-flex" }}
        tabIndex={0}
        role="group"
        aria-label={t("Other people viewing this document")}
      >
        <AvatarGroup
          max={row.maxVisibleAvatars + 1}
          spacing={row.spacing}
          sx={{
            "& .MuiAvatar-root": { ...row.avatar, borderColor: "inherit" },
          }}
        >
          {members.map(member => (
            <Avatar
              key={member.id}
              src={member.avatar?.url ?? undefined}
              alt={member.fullname}
              aria-label={member.fullname}
            >
              {getInitials(member.firstname, member.familyname)}
            </Avatar>
          ))}
        </AvatarGroup>
      </Box>
    </Tooltip>
  );
};
